
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { veeValidator } from '@/mixins/veeValidator'
import { filterForExclusions } from '@/modules/Territories'
import { compileStyles } from '@/modules/compileStyles'
import {
  buildSplititCustomer,
  buildSplititAddress,
  buildSplititInitiateBody,
  buildSplititOrderPayload,
  extractSplititErrorMessage,
  submitSplititPlaceOrder
} from '@/modules/CheckoutReduxWithSplititHelpers'
import AddressFormBilling from './AddressFormBilling'
import CreditCardForm from './CreditCardForm'
import PriceInfoWithSplitit from './PriceInfoWithSplitit'
import SMSSignup from './SMSSignup'
import SplititHostedFields from './SplititHostedFields'
export default {
  name: 'CheckoutFormReduxWithSplitit',
  components: {
    CreditCardForm,
    AddressFormBilling,
    PriceInfoWithSplitit,
    SMSSignup,
    SplititHostedFields,
    Discount: () => import('./Discount'),
    Quantity: () => import('./Quantity'),
    PaymentsOptionsWithSplitit: () => import('./PaymentsOptionsWithSplitit'),
    PaymentsModal: () => import('@/components/common/PaymentsModal')
  },
  mixins: [veeValidator],
  props: {
    blok: {
      required: true,
      type: Object
    }
  },
  data() {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        billingInfo: {
          address: '',
          city: '',
          country: 'US',
          state: '',
          zip: ''
        },
        shippingInfo: {
          address: '',
          city: '',
          country: 'US',
          state: '',
          zip: ''
        },
        creditCardInfo: {
          number: '',
          expiresOnMonth: '',
          expiresOnYear: '',
          CVV2: ''
        },
        billingAddressDifferent: false,
        quantity: 1,
        productId: parseInt(this.blok.product_id.selected),
        promoCode: '',
        smsConsent: false,
        smsPageName: this.blok.keyword.keyword || 'NSPCOP'
      },
      show: true,
      shipToCanada: false,
      loading: false,
      splititIpn: null,
      splititStickyOrderId: null,
      // PID splitit was activated on; kept across full-pay↔splitit toggles
      // to avoid re-initiating on every flip. Cleared when the PID changes.
      splititActivatedPid: null,
      paymentsFullPid: 0,
      hasPayments: false,
      completelySoldOut: false,
      modal: { show: false },
      discountGPM: null,
      offerPair: [],
      buttonDisabled: false,
      blotoutShipping: false,
      blotoutInit: false
    }
  },
  computed: {
    customTerritories() {
      const exclusions = this.offer(this.form.productId).shipping.exclusions
      return filterForExclusions(exclusions)
    },
    customValidators() {
      const exclusions = this.offer(this.form.productId).shipping.exclusions
      return exclusions.includes('poBox') ? { address: 'pobox' } : {}
    },
    checkPayments() {
      return pid => {
        let hasPayments = false
        this.offers.forEach(offer => {
          if (
            (offer.payments.paymentPlanFor === parseInt(pid) &&
              offer.pricing.isPaymentPlan) ||
            (offer.product.id === parseInt(pid) && offer.pricing.isPaymentPlan)
          ) {
            hasPayments = true
          }
        })
        return hasPayments
      }
    },
    isPaymentPlan() {
      return this.offer(this.form.productId).pricing.isPaymentPlan
    },
    discountCodes() {
      return {
        fullPrice: this.blok.DiscountCode,
        payments: this.blok.payments_discount_code
      }
    },
    style() {
      return compileStyles(this.blok.style || {}, this.$vuetify.breakpoint.name)
    },
    // determine presence of expiredTimer/permanent timer
    expiredTimerShow() {
      return this.$store.getters['timer/expiredTimers'].find(
        id => id === this.blok._uid
      )
    },
    numberOfInstallments() {
      return this.offer(this.form.productId).payments.numberOfPayments || 0
    },
    // billingInfo is empty until the user opts into a separate billing
    // address; splitit initiates before submit copies it, so resolve here.
    effectiveForm() {
      if (this.form.billingAddressDifferent) return this.form
      return { ...this.form, billingInfo: this.form.shippingInfo }
    },
    splititCustomer() {
      return buildSplititCustomer(this.form)
    },
    splititBillingAddress() {
      return buildSplititAddress(this.effectiveForm.billingInfo)
    },
    splititShippingAddress() {
      return buildSplititAddress(this.form.shippingInfo)
    },
    splititInitiateBody() {
      return buildSplititInitiateBody({
        state: this.$store.state,
        getters: this.$store.getters,
        form: this.effectiveForm
      })
    },
    ...mapGetters([
      'offer',
      'offers',
      'pricing',
      'busy',
      'confirmedPurchase',
      'paymentError',
      'paymentMode'
    ])
  },
  beforeMount() {
    if (this.blok.action === 'show') {
      this.show = false
    }
    if (
      this.blok.permanent &&
      this.$store.getters['timer/expiredTimers'].find(
        id => id === this.blok._uid
      )
    ) {
      this.show = this.blok.action !== 'hide'
    }
  },
  mounted() {
    this.updateOfferPair()
    this.decideAddTimer()
    this.$bus.$on('update-payment-mode', this.handleUpdatePaymentMode)
    this.$bus.$on('update-pid', this.handleUpdatePid)
    this.$bus.$on('set-coupon', this.handleSetCoupon)
    this.$bus.$on('change-coupon', this.handleChangeCoupon)
    this.$bus.$on('decline-coupon', this.handleDeclineCoupon)
    this.$bus.$on('clear-discountGPM', this.handleClearDiscountGPM)
    this.$bus.$on('update-quantity', this.handleUpdateQuantity)
    this.$bus.$on('completelySoldOut', this.handleCompletelySoldOut)
    this.$bus.$on('accept-SMS', this.handleAcceptSMS)
    this.$bus.$on('decline-SMS', this.handleDeclineSMS)
    this.$bus.$on('updateGPM', this.handleUpdateGPM)
    this.$bus.$on('google-disable', this.handleGoogleDisable)
    this.$bus.$on('google-corrected', this.handleGoogleCorrected)

    const kaScript = document.createElement('script')
    const fullSessionId = this.$store.getters.sessionId
    const kountUrl = this.$config.kountDataCollector
    kaScript.setAttribute('src', kountUrl + fullSessionId)
    document.head.appendChild(kaScript)

    window.addEventListener('keydown', this.blotoutEvent, { once: true })
    // Lets shared components (e.g. AddressFormBilling's tax-estimate) opt
    // into splitit-aware pricing; stays false for legacy checkouts.
    this.setSplititCheckoutMounted(true)
  },
  beforeDestroy() {
    this.$bus.$off('update-payment-mode', this.handleUpdatePaymentMode)
    this.$bus.$off('update-pid', this.handleUpdatePid)
    this.$bus.$off('set-coupon', this.handleSetCoupon)
    this.$bus.$off('change-coupon', this.handleChangeCoupon)
    this.$bus.$off('decline-coupon', this.handleDeclineCoupon)
    this.$bus.$off('clear-discountGPM', this.handleClearDiscountGPM)
    this.$bus.$off('update-quantity', this.handleUpdateQuantity)
    this.$bus.$off('completelySoldOut', this.handleCompletelySoldOut)
    this.$bus.$off('accept-SMS', this.handleAcceptSMS)
    this.$bus.$off('decline-SMS', this.handleDeclineSMS)
    this.$bus.$off('updateGPM', this.handleUpdateGPM)
    this.$bus.$off('google-disable', this.handleGoogleDisable)
    this.$bus.$off('google-corrected', this.handleGoogleCorrected)
    window.removeEventListener('keydown', this.blotoutEvent)
    // Reset global flags so a non-splitit next step doesn't inherit our
    // mode (enterStep also does this, but the form can unmount via v-if).
    this.setPaymentMode('full-pay')
    this.setSplititCheckoutMounted(false)
  },
  methods: {
    clearCoupon() {
      this.form.promoCode = ''
    },
    // goTo throws if the target isn't mounted yet; swallow so a missing
    // scroll target never blocks the submit/error flow.
    scrollTo(selector = '.v-alert__content') {
      try {
        this.$vuetify.goTo(selector, { offset: 50 })
      } catch (e) {}
    },
    // Shared pre-submit housekeeping for every payment path.
    finalizeFormBeforeSubmit() {
      if (!this.form.billingAddressDifferent) {
        this.form.billingInfo = this.form.shippingInfo
      }
      if (this.acceptSMS === true) {
        // Fire and forget — an SMS opt-in failure must never block or reject
        // into the payment path below.
        this.$salesApi
          .subscribe(this.form.phone, this.form.smsPageName, this.form.email)
          .catch(() => {})
      }
      if (this.discountGPM) {
        this.$store.getters.offer(
          this.form.productId
        ).pricing.metric1 = this.discountGPM
      }
      this.setCustomer(this.form)
    },
    async handleUpdatePaymentMode(mode) {
      // Ignore no-op re-broadcasts (notifyMode re-emits the current mode
      // after rebuilds); running the clear-error path would erase the alert.
      if (mode === this.paymentMode) return
      if (mode === 'splitit-plan') {
        const valid = await this.validateAddressForSplitit()
        if (!valid) {
          this.setPaymentError(
            'Please complete your information below to view payment plan details'
          )
          this.$bus.$emit('revert-payment-mode', 'full-pay')
          this.$nextTick(() => this.scrollTo())
          return
        }
        // Latch the activation PID so re-selecting splitit on the same PID
        // reuses the mounted form (no re-initiate); a variant change moves
        // off the latch and unmounts FlexForm.
        this.splititActivatedPid = parseInt(this.form.productId)
      }
      // Mode actually transitioning — clear any prior alert and commit.
      this.setPaymentError(null)
      this.setPaymentMode(mode)
    },
    handleUpdatePid(pid) {
      // On a plan radio, remap a variant change to the new variant's plan
      // equivalent so the customer stays on a plan, not its full-pay anchor.
      const effectivePid = this.pidPreservingPlanAcrossVariants(pid)
      this.form.productId = effectivePid.toString()
      // Variant change on splitit-plan: re-latch the new anchor so the form
      // remounts and re-initiates against the new amount. A non-splitit
      // variant gets downshifted to full-pay by the options rebuild.
      if (this.paymentMode === 'splitit-plan') {
        const nextOffer = this.offer(parseInt(effectivePid))
        if (
          nextOffer &&
          nextOffer.payments &&
          nextOffer.payments.paymentPlanType === 'splitit' &&
          nextOffer.payments.paymentPlanFor === 0
        ) {
          this.splititActivatedPid = parseInt(effectivePid)
        }
      }
      if (!this.show) return

      this.updateOfferPair()
      if (this.form.promoCode && this.form.promoCode !== 'nonvalidCode') {
        this.$bus.$emit('change-coupon', effectivePid)
      } else if (this.form.promoCode === 'nonvalidCode') {
        this.form.promoCode = ''
      }
      setTimeout(() => {
        this.$bus.$emit('update-payment-options', effectivePid)
      }, 100)
      this.updatePidToString()
    },
    handleSetCoupon() {
      if (!this.show) return
      this.form.promoCode = this.offer(this.form.productId).pricing
        .isPaymentPlan
        ? this.blok.payments_discount_code
        : this.blok.DiscountCode
      this.$bus.$emit('update-coupon', {
        promoCode: this.form.promoCode,
        pid: this.form.productId
      })
    },
    handleChangeCoupon(pid) {
      if (!this.show) return
      this.form.promoCode =
        this.offer(this.form.productId).pricing.isPaymentPlan &&
        this.blok.payments_discount_code
          ? this.blok.payments_discount_code
          : this.offer(this.form.productId).pricing.isPaymentPlan &&
            !this.blok.payments_discount_code
          ? 'nonvalidCode'
          : this.blok.DiscountCode
      this.$bus.$emit('update-coupon', {
        promoCode: this.form.promoCode,
        pid
      })
    },
    handleDeclineCoupon() {
      if (this.show) this.clearCoupon()
    },
    handleClearDiscountGPM() {
      if (this.show) this.discountGPM = null
    },
    handleUpdateQuantity(quantity) {
      if (this.show) this.form.quantity = quantity
    },
    handleCompletelySoldOut() {
      if (this.show) this.completelySoldOut = true
    },
    handleAcceptSMS() {
      this.form.smsConsent = true
      if (this.show) this.acceptSMS = true
    },
    handleDeclineSMS() {
      this.form.smsConsent = false
      if (this.show) this.acceptSMS = false
    },
    handleUpdateGPM(discount) {
      if (!this.show) return
      this.discountGPM =
        this.$store.getters.offer(this.form.productId).pricing.metric1 -
        discount
    },
    handleGoogleDisable() {
      this.buttonDisabled = true
    },
    handleGoogleCorrected() {
      this.buttonDisabled = false
    },
    updateOfferPair() {
      this.offerPair = []
      this.offers.forEach(offer => {
        if (
          offer.product.id === parseInt(this.form.productId) ||
          (offer.payments.paymentPlanFor === parseInt(this.form.productId) &&
            offer.pricing.isPaymentPlan)
        ) {
          this.offerPair.push(offer)
        }
      })
    },
    decideAddTimer() {
      if (!!this.blok.timer && this.blok.timer.timeUnit !== 'disabled') {
        this.$store.dispatch('timer/addEvent', {
          deadline: {
            id: this.blok._uid,
            seconds:
              Number(this.blok.timer.minutes * 60) +
              Number(this.blok.timer.seconds),
            isDateTime: this.blok.timer.timeUnit === 'dateTime',
            dateTime: this.blok.timer.dateTime,
            permanent: this.blok.permanent,
            callback: () => {
              this.show = this.blok.action !== 'hide'
              this.splititActivatedPid = null
              this.setPaymentMode('full-pay')
              this.$bus.$emit('revert-payment-mode', 'full-pay')
              // Use paymentPlanFor (not isPaymentPlan) to tell a sticky
              // companion from a splitit anchor: both now set isPaymentPlan,
              // so the old check flipped the customer's PID (POV-1501).
              const currentOffer = this.offer(this.form.productId)
              if (!currentOffer || !currentOffer.payments) return
              const onCompanion = currentOffer.payments.paymentPlanFor !== 0
              if (onCompanion) {
                this.offerPair.forEach(offer => {
                  if (offer.payments.paymentPlanFor !== 0) {
                    this.$bus.$emit('update-pid', offer.product.id)
                  }
                })
              } else {
                this.offerPair.forEach(offer => {
                  if (offer.payments.paymentPlanFor === 0) {
                    this.$bus.$emit('update-pid', offer.product.id)
                  }
                })
              }
            }
          }
        })
      }
    },
    updatePidToString(pid) {
      const payInfo = this.offer(pid)
      this.hasPayments = payInfo.payments.paymentPlanFor
      this.numberOfPayments = payInfo.payments.numberOfPayments - 1
      this.paymentPrice = this.$store.getters.coalescedPrice(payInfo)
    },
    callToAction() {
      if (this.paymentMode === 'splitit-plan') {
        // Splitit replaces the TILA modal and CC path; submitOrder
        // validates then hands off to FlexForm.
        this.submitOrder()
      } else if (
        this.paymentMode === 'sticky-plan' &&
        this.offer(this.form.productId).pricing.isPaymentPlan
      ) {
        this.$validator.validateAll().then(success => {
          if (success) {
            this.openMyDialog()
          } else {
            this.scrollTo('.error--text')
          }
        })
      } else {
        this.submitOrder()
      }
      // eslint-disable-next-line no-undef
      const client = new ka.ClientSDK()
      client.setupCallback({
        'collect-begin': function(params) {
          const checkoutForm = document.forms.checkoutForm
          const input = document.createElement('input')
          input.type = 'hidden'
          input.name = 'sessionId'
          input.value = params.MercSessId
          checkoutForm.appendChild(input)
        }
      })
      client.autoLoadEvents()
    },
    submitOrder(TILAAgreement) {
      this.$validator.validateAll().then(success => {
        if (success) {
          if (this.paymentMode === 'splitit-plan') {
            // CC normalize and TILA assignment are skipped — FlexForm
            // captures the card and Splitit replaces the TILA modal flow.
            this.finalizeFormBeforeSubmit()
            this.submitSplititOrder()
            return
          }

          this.form.creditCardInfo.number = this.form.creditCardInfo.number.replace(
            /-/g,
            ''
          )
          if (this.offer(this.form.productId).pricing.isPaymentPlan) {
            this.modal.show = false
          }
          if (typeof TILAAgreement !== 'undefined') {
            Object.assign(this.form, TILAAgreement)
          }

          this.finalizeFormBeforeSubmit()

          this.purchase(this.form).then(({ errorCode }) => {
            if (errorCode) {
              this.scrollTo()
              if (errorCode === 800) {
                this.form.creditCardInfo.number = ''
                this.form.creditCardInfo.expiresOnMonth = ''
                this.form.creditCardInfo.expiresOnYear = ''
                this.form.creditCardInfo.CVV2 = ''
              } else if (errorCode === 342) {
                this.form.creditCardInfo.expiresOnMonth = ''
                this.form.creditCardInfo.expiresOnYear = ''
              }
            }
          })
        } else {
          this.scrollTo('.error--text')
        }
      })
    },
    // An offer's "group" is its full-price anchor PID: its own PID if it's
    // the anchor (paymentPlanFor === 0), else the anchor it points at.
    offerGroupOf(offer) {
      if (!offer || !offer.payments) return null
      return offer.payments.paymentPlanFor === 0
        ? offer.product.id
        : offer.payments.paymentPlanFor
    },
    // Keep the customer on their plan when the variant changes. Splitit-plan
    // shares the anchor's PID, so the inbound PID is already right; sticky
    // has its own PID, so substitute the new group's sticky companion.
    pidPreservingPlanAcrossVariants(pid) {
      if (this.paymentMode === 'full-pay') return pid
      const currentOffer = this.offer(this.form.productId)
      const nextOffer = this.offer(pid)
      if (!currentOffer || !nextOffer) return pid
      const currentGroup = this.offerGroupOf(currentOffer)
      const nextGroup = this.offerGroupOf(nextOffer)
      if (currentGroup === nextGroup) return pid
      if (this.paymentMode === 'sticky-plan') {
        const stickyForNew = this.offers.find(
          o =>
            o.payments.paymentPlanFor === pid &&
            (o.payments.paymentPlanType === 'sticky' ||
              !o.payments.paymentPlanType)
        )
        if (stickyForNew) return stickyForNew.product.id
      }
      return pid
    },
    updatePid(value) {
      this.form.productId = value.toString()
    },
    validateAddressForSplitit() {
      // Country has data-vv-name but no v-validate, so it isn't registered
      // with the parent validator — omit it.
      const fields = [
        'firstName',
        'lastName',
        'email',
        'phone',
        'shippingAddress',
        'shippingCity',
        'shippingState',
        'shippingZip'
      ]
      if (this.form.billingAddressDifferent) {
        fields.push(
          'billingAddress',
          'billingCity',
          'billingState',
          'billingZip'
        )
      }
      return Promise.all(
        fields.map(f => this.$validator.validate(f))
      ).then(results => results.every(Boolean))
    },
    async submitSplititOrder() {
      if (!this.$refs.splitit) {
        this.setPaymentError('Splitit form is not ready. Please try again.')
        return
      }
      this.setBusy(true)
      // If details changed since initiate, re-bootstrap against current data;
      // the iframe is destroyed in the process, so the user must re-enter
      // their card (handled below).
      let reinitiated
      try {
        reinitiated = await this.$refs.splitit.reinitIfOrderDetailsChanged()
      } catch (err) {
        this.onSplititError(err)
        return
      }
      if (reinitiated) {
        this.setBusy(false)
        this.setPaymentError(
          'We refreshed your payment plan with your updated details. ' +
            'Please re-enter your card and submit again'
        )
        this.$nextTick(() => this.scrollTo())
        return
      }
      // pay() returns false on invalid fields (reset busy, scroll back) and
      // true when a charge is attempted (onSuccess/onError reset busy).
      let attempted
      try {
        attempted = await this.$refs.splitit.pay()
      } catch (err) {
        this.onSplititError(err)
        return
      }
      if (!attempted) {
        this.setBusy(false)
        this.$nextTick(() => this.scrollTo('#splitit-payment-form'))
      }
    },
    async onSplititSuccess(ipn) {
      this.splititIpn = ipn
      const order = buildSplititOrderPayload({
        state: this.$store.state,
        getters: this.$store.getters,
        form: this.effectiveForm
      })
      const result = await submitSplititPlaceOrder({
        salesApi: this.$salesApi,
        splititIpn: ipn,
        stickyOrderId: this.splititStickyOrderId,
        order,
        splitTestFunnel: this.$store.getters['split-testing/splitTestFunnel']
      })
      if (result.ok) {
        this.completeOrderConfirmation(result.data, order)
      } else {
        this.setPaymentError(result.message)
        if (result.err && this.$bugsnag) this.$bugsnag.notify(result.err)
      }
      this.setBusy(false)
    },
    onSplititError(err) {
      const message = extractSplititErrorMessage(err)
      this.setPaymentError(message)
      if (this.$bugsnag) {
        this.$bugsnag.notify(err instanceof Error ? err : new Error(message))
      }
      this.setBusy(false)
      this.scrollTo()
    },
    onSplititInitiated({ stickyOrderId } = {}) {
      this.splititStickyOrderId = stickyOrderId || null
    },
    completeOrderConfirmation(response, order) {
      const data = (response && response.data) || response || {}
      const confirmation = {
        orderId: data.orderId,
        orderTotal: data.orderTotal,
        customerId: data.customerId,
        orderSalesTaxAmount: data.orderSalesTaxAmount,
        ...order
      }
      confirmation.pricing = this.pricing(order.productId, order.quantity)
      confirmation.offer = this.offer(order.productId, order.quantity)
      this.$store.commit('addOrder', confirmation)
      this.$store
        .dispatch('firePurchaseAnalytics', confirmation)
        .catch(err => {
          if (this.$bugsnag) {
            this.$bugsnag.notify(
              err instanceof Error ? err : new Error(String(err))
            )
          }
        })
        .finally(() => {
          this.$store.dispatch('nextStep', {
            acceptOffer: true,
            productId: order.productId,
            isSplitit: !!this.splititIpn
          })
        })
    },
    openMyDialog() {
      // TILA modal is sticky-plan-only. Splitit and full-pay must never trigger it.
      if (this.paymentMode !== 'sticky-plan') {
        this.submitOrder()
        return
      }
      if (
        // triggers modal on 4+ payments, needs to be explicit for legal reasons
        this.offer(this.form.productId).payments.numberOfPayments >= 4 ||
        // triggers modal on > 4 payments and if there are items in the form array
        (this.offer(this.form.productId).payments.numberOfPayments < 4 &&
          Array.isArray(
            this.offer(this.form.productId).payments.paymentModalCheckboxes
          ) &&
          this.offer(this.form.productId).payments.paymentModalCheckboxes
            .length >= 1)
      ) {
        this.modal.show = true
      } else {
        this.submitOrder()
      }
    },
    ...mapActions({
      purchase: 'purchase'
    }),
    ...mapMutations({
      setCustomer: 'setCustomer',
      setBusy: 'setBusy',
      setPaymentError: 'setPaymentError',
      setPaymentMode: 'setPaymentMode',
      setSplititCheckoutMounted: 'setSplititCheckoutMounted'
    }),
    getBlotoutPayload() {
      const category = this.$store.getters.route.productLine
      const url = this.$store.getters['analytics/url'].base
      const offer = this.$store.getters.offer(this.form.productId)
      return {
        currency: 'USD',
        checkoutUrl: url,
        contents: [
          {
            id: offer.product.id,
            quantity: 1,
            item_price: this.$store.getters.coalescedPrice(offer),
            title: offer.name,
            category,
            url
          }
        ]
      }
    },
    blotoutEvent() {
      if (this.show && !this.blotoutInit) {
        this.$blotout.sendEvent('InitiateCheckout', this.getBlotoutPayload())
        this.blotoutInit = true
      }
      // emits from address form
      this.$bus.$on('blotout-address', () => {
        if (this.show && !this.blotoutShipping) {
          this.$blotout.sendEvent('AddShippingInfo', this.getBlotoutPayload())
          this.blotoutShipping = true
        }
      })
    }
  }
}
